<template>
  <div
    :class="['folder-selector q-gutter-md', { flex: isEdit }]"
    :style="styles"
  >
    <AppButton
      v-for="folder in systemFolders"
      :key="folder.label"
      :label="folder.label"
      :class="['text-15-400 no-hover', { 'btn-selected': isSelectedFolder(folder.value) }]"
      label-class=""
      padding="10px 20px"
      text-color="black"
      outline
      @click="onToggleFolder(folder.value)"
    />
    <template v-if="isEdit">
      <AppInput
        v-for="(folder, index) in updatedFolders"
        :key="folder.guid"
        v-model="updatedFolders[index].title"
        :disabled="loading.update"
        class="text-15-400"
      >
        <template #append>
          <q-icon
            color="grey-7"
            class="cursor-pointer"
            name="fl:trash"
            size="20px"
            @click="onShowRemoveDialog(folder)"
          />
        </template>
      </AppInput>
    </template>
    <template v-else>
      <AppButton
        v-for="folder in folders"
        :key="folder.guid"
        :label="folder.title"
        label-class=""
        :class="['text-15-400 no-hover', { 'btn-selected': isSelectedFolder(folder) }]"
        padding="10px 20px"
        text-color="black"
        outline
        @click="onToggleFolder(folder)"
      />
    </template>
    <template v-if="isEdit">
      <AppButton
        class="no-hover"
        icon-left="close"
        icon-left-size="20px"
        icon-left-color="grey-7"
        padding="13px"
        outline
        @click="isEdit = false"
      />
      <AppButton
        :loading="loading.update"
        icon-left="fl:done"
        icon-left-size="20px"
        icon-left-color="white"
        padding="13px"
        color="primary"
        @click="onUpdateFolders"
      />
    </template>
    <template v-else>
      <AppButton
        class="no-hover"
        icon-left="fl:add_round"
        icon-left-size="20px"
        icon-left-color="grey-7"
        padding="13px"
        outline
        @click="showCreateDialog = true"
      />
      <AppButton
        icon-left="fl:pen"
        icon-left-size="20px"
        icon-left-color="grey-7"
        padding="13px"
        color="white"
        flat
        @click="onEdit"
      />
    </template>

    <!-- Create dialog -->
    <AttentionDialog
      v-if="showCreateDialog"
      :title="$t('title.createCategory')"
      img="add_folder.svg"
      img-width="151px"
      is-open
      @close="onCancel"
    >

      <div class="q-pa-lg">
        <AppInput
          v-model="categoryName"
          :placeholder="$t('placeholder.nameCategory')"
          :rules="[rules.REQUIRED]"
          :disabled="loading.create"
          @keyup.enter="onCreateFolder"
        />
      </div>
      <template #footer>
        <AppButton
          v-close-popup
          :label="$t('button.cancel')"
          class="col"
          color="white"
          text-color="black"
          outline
        />
        <AppButton
          :label="$t('button.create')"
          :loading="loading.create"
          label-class="q-px-sm"
          class="col"
          icon-right="fl:folder_add"
          icon-right-size="24px"
          @click="onCreateFolder"
        />
      </template>
    </AttentionDialog>

    <!-- Remove dialog -->
    <AttentionDialog
      v-if="showRemoveDialog"
      :title="$t('title.removeCategory')"
      is-open
      @close="onCancel"
    >
      <div class="row justify-center">
        <div class="col-8">
          <div class="text-19-400 text-center q-py-lg">
            {{ $t('text.deleteCategoryConfirm', [removingFolder.title]) }}
          </div>
        </div>
      </div>
      <template #footer>
        <AppButton
          v-close-popup
          :label="$t('button.cancel')"
          class="col"
          color="white"
          text-color="black"
          outline
        />
        <AppButton
          :label="$t('button.remove')"
          :loading="loading.delete"
          label-class="q-px-sm"
          class="col"
          icon-right="fl:trash"
          icon-right-size="24px"
          @click="onRemoveFolder"
        />
      </template>
    </AttentionDialog>
  </div>
</template>

<script>
import AppInput from 'common/components/AppInput';
import AppButton from 'common/components/buttons/AppButton';
import AttentionDialog from 'common/components/dialogs/AttentionDialog';
import handleError from 'common/helpers/handleError';
import { cloneObject } from 'common/helpers/objectHelper';
import rules from 'common/helpers/rules';
import { colors } from 'quasar';
import { mapActions, mapGetters, mapMutations } from 'vuex';

export default {
  name: 'FolderSelector',
  components: {
    AppButton,
    AppInput,
    AttentionDialog,
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => Object.values(ENUMS.FOLDER_TYPES).includes(value),
    },
  },
  emits: ['fetch'],
  data() {
    return {
      isEdit: false,
      showCreateDialog: false,
      categoryName: undefined,
      removingFolder: undefined,
      systemFolders: [
        {
          label: this.$t('button.all'),
          value: undefined,
        },
        {
          label: this.$t('button.uncategorized'),
          value: '',
        },
      ],
      updatedFolders: [],
      loading: {
        create: false,
        delete: false,
        update: false,
      },
    };
  },
  computed: {
    ...mapGetters('dashboard', ['getFolders', 'getSelectedFolders']),
    folders() {
      return this.getFolders(this.type);
    },
    selectedFolders: {
      get() {
        return this.getSelectedFolders(this.type);
      },
      set(payload) {
        this.setSelectedFolders({
          type: this.type,
          payload,
        });
      },
    },
    rules: () => rules,
    styles() {
      return {
        '--active-bg': colors.changeAlpha(colors.getBrand('primary'), 0.2),
      };
    },
    showRemoveDialog() {
      return this.removingFolder !== undefined;
    },
    endpoint() {
      return `folders/${this.type}`;
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    ...mapActions('dashboard', ['fetchFolders']),
    ...mapMutations('dashboard', ['setSelectedFolders']),
    async fetch() {
      try {
        await this.fetchFolders({
          type: this.type,
          params: {
            perPage: 100,
          },
        });
      } catch (error) {
        handleError(error);
      }
    },
    async onCreateFolder() {
      if (!this.categoryName) {
        return;
      }
      try {
        this.loading.create = true;
        await this.$axios.post(this.endpoint, { title: this.categoryName });
        await this.fetch();
        this.onCancel();
      } catch (error) {
        handleError(error);
      } finally {
        this.loading.create = false;
      }
    },
    async onRemoveFolder() {
      try {
        this.loading.delete = true;
        await this.$axios.delete(`${this.endpoint}/${this.removingFolder.guid}`);
        // We need to update form/page list with their new folders
        this.$emit('fetch');

        // Delete folder from selected folders if it exists
        if (this.isSelectedFolder(this.removingFolder)) {
          this.onToggleFolder(this.removingFolder);
        }
        await this.fetch();
        this.updatedFolders = this.updatedFolders.filter(({ guid }) => guid !== this.removingFolder.guid);
        this.onCancel();
      } catch (error) {
        handleError(error);
      } finally {
        this.loading.delete = false;
      }
    },
    async onUpdateFolders() {
      try {
        this.loading.update = true;
        const changedFolders = this.updatedFolders.filter((folder) => {
          const source = this.folders.find((sourceFolder) => sourceFolder.guid === folder.guid);
          return folder.title !== source.title;
        });
        if (changedFolders.length > 0) {
          await Promise.all(changedFolders.map(({ guid, title }) => this.$axios.put(`${this.endpoint}/${guid}`, { title })));
          await this.fetch();
        }
        this.isEdit = false;
        this.updatedFolders = [];
      } catch (error) {
        handleError(error);
      } finally {
        this.loading.update = false;
      }
    },
    onToggleFolder(folder) {
      if (folder === undefined || folder === '') {
        this.selectedFolders = folder;
      } else {
        let folders;
        if (!Array.isArray(this.selectedFolders)) {
          folders = [];
        }
        if (this.isSelectedFolder(folder)) {
          folders = this.selectedFolders.filter((guid) => guid !== folder.guid);
        } else {
          folders = [
            ...(this.selectedFolders || []),
            folder.guid,
          ];
        }
        this.selectedFolders = folders.length > 0 ? folders : undefined;
      }
    },
    onShowRemoveDialog(folder) {
      this.removingFolder = folder;
    },
    onCancel() {
      this.showCreateDialog = false;
      this.removingFolder = undefined;
      this.categoryName = undefined;
    },
    onEdit() {
      this.isEdit = true;
      this.updatedFolders = cloneObject(this.folders);
    },
    isSelectedFolder(folder) {
      if (folder === undefined || folder === '') {
        return this.selectedFolders === folder;
      }
      if (Array.isArray(this.selectedFolders)) {
        return this.selectedFolders.includes(folder.guid);
      }
      return false;
    },
  },
};
</script>

<style scoped lang="scss">
.folder-selector {
  .btn-selected {
    background: var(--active-bg) !important;
  }

  .btn-selected,
  .q-btn:not(.q-btn--flat):hover {
    ::v-deep .q-btn__wrapper:before {
      border: 1px solid $primary;
    }
  }
  .q-btn--flat:hover {
    background: $grey-3 !important;
  }

  ::v-deep {
    .q-btn__wrapper:before {
      transition: all 0.25s ease;
    }

    .q-field__control {
      &:after,
      &:before {
        border-width: 1px;
      }

      height: 46px !important;
      padding: 0 20px !important;
      font-size: 15px;
    }

    .q-field--highlighted .q-field__control:before {
      border-color: $primary;
      border-width: 1px;
    }
  }
}
</style>
